import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaEnvelope, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.png';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error(t('errors.fillAllFields') || 'Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('errors.invalidEmail') || 'Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await forgotPassword(email);
      setIsSuccess(true);
      toast.success(t('auth.resetEmailSent') || 'Password reset email sent! Check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      let errorMessage = t('errors.resetFailed') || 'Failed to send reset email';

      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = t('errors.userNotFound') || 'No account found with this email address';
          break;
        case 'auth/invalid-email':
          errorMessage = t('errors.invalidEmail') || 'Invalid email address';
          break;
        case 'auth/too-many-requests':
          errorMessage = t('errors.tooManyRequests') || 'Too many requests. Please try again later.';
          break;
        default:
          errorMessage = error.message || errorMessage;
      }

      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-white to-[#EFF6FF] flex items-center justify-center p-4 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-30"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100 rounded-full translate-x-1/2 translate-y-1/2 opacity-30"></div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center relative z-10"
        >
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <FaCheckCircle className="w-10 h-10 text-green-600" />
          </motion.div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {t('auth.resetEmailSentTitle') || 'Check Your Email'}
          </h1>

          <p className="text-gray-600 mb-6 leading-relaxed">
            {t('auth.resetEmailSentDesc') ||
             'We\'ve sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.'}
          </p>

          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              {t('auth.didntReceiveEmail') || 'Didn\'t receive the email?'}
            </p>

            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <FaSpinner className="w-4 h-4 animate-spin" />
              ) : (
                <FaEnvelope className="w-4 h-4" />
              )}
              {t('auth.resendEmail') || 'Resend Email'}
            </button>

            <button
              onClick={handleBackToLogin}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <FaArrowLeft className="w-4 h-4" />
              {t('auth.backToLogin') || 'Back to Login'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-white to-[#EFF6FF] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-100 rounded-full -translate-x-1/2 -translate-y-1/2 opacity-30"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100 rounded-full translate-x-1/2 translate-y-1/2 opacity-30"></div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 relative z-10"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
            className="flex justify-center mb-4"
          >
            <img src={logo} alt="CVLogs" className="w-16 h-16 object-contain" />
          </motion.div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {t('auth.forgotPassword') || 'Forgot Password?'}
          </h1>

          <p className="text-gray-600">
            {t('auth.resetInstructions') ||
             'Enter your email address and we\'ll send you a link to reset your password.'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.email') || 'Email Address'}
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder') || 'Enter your email'}
                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                disabled={isLoading}
                required
              />
              <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>
          </motion.div>

          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <FaSpinner className="w-4 h-4 animate-spin" />
                {t('auth.sending') || 'Sending...'}
              </>
            ) : (
              <>
                <FaEnvelope className="w-4 h-4" />
                {t('auth.sendResetLink') || 'Send Reset Link'}
              </>
            )}
          </motion.button>
        </form>

        {/* Back to Login */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-6 text-center"
        >
          <button
            onClick={handleBackToLogin}
            className="text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-2 mx-auto transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
            {t('auth.backToLogin') || 'Back to Login'}
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;